import React, { useCallback, useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useHistory } from "react-router-dom";
import Card from "components/Card";
import Page from "components/Page";
import Text from "components/Text";
import { decodeId } from "utils";
import constants from "../../../../../constants";
import {
  usePolicyAssignmentById,
  usePolicyAssignmentComments,
  addPolicyAssignmentComment,
} from "repositories/office-policy-management-repository";
import { useQueryClient } from "react-query";
import toast from "react-hot-toast";
import styles from "../../OfficePolicyManagement.module.scss";
import LeftSection from "./LeftSection";
import RightSection from "./RightSection";
import Empty from "components/Empty";
import Loader from "components/Loader";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";
import { uniqBy } from "lodash";

const COMMENT_PAGE_SIZE = constants.pageSize;

const sortCommentsAscending = (items) =>
  [...(items || [])].sort(
    (a, b) =>
      new Date(a?.createdAt || 0).getTime() -
      new Date(b?.createdAt || 0).getTime(),
  );

const SHELL_CARD_CLASS = ` ${styles["task_details_card"]} `;
const POLICY_DETAIL_CARD_CLASS = ` ${styles["task_details_card"]} ${styles["policy-detail-card"]}  `;

/**
 * Banner listing offices this policy applies to.
 * @param {{ appliesToHeading: string; officesLabel: string | null }} props
 */
function PolicyScopeBanner({ appliesToHeading, officesLabel }) {
  if (!officesLabel) return null;
  return (
    <div
      className={` ${styles["policy-banner"]} ${styles["policy-banner-top"]} `}
    >
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path
          d="M12 1.99609C17.5228 1.99609 22 6.47325 22 11.9961C22 17.5189 17.5228 21.9961 12 21.9961C6.47715 21.9961 2 17.5189 2 11.9961C2 6.47325 6.47715 1.99609 12 1.99609Z"
          stroke="black"
          strokeWidth="1.96364"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M12 15.9961V11.9961"
          stroke="black"
          strokeWidth="1.96364"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M12 8.00391H12.0088"
          stroke="black"
          strokeWidth="1.96364"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span>
        <strong>{appliesToHeading}</strong> {officesLabel}
      </span>
    </div>
  );
}

/**
 * Normalizes policy comments API payloads (shape varies by endpoint/version).
 * @param {unknown} commentsData
 * @returns {{ rawComments: unknown[], totalFromApi: number }}
 */
function parseCommentsResponse(commentsData) {
  const rawComments =
    commentsData?.data ??
    commentsData?.items ??
    (Array.isArray(commentsData) ? commentsData : []);
  const pagination =
    commentsData?.pagination ?? commentsData?.data?.pagination ?? {};
  const totalFromApi =
    pagination?.totalItems ??
    pagination?.totalCount ??
    (Array.isArray(rawComments) ? rawComments.length : 0);
  return { rawComments, totalFromApi };
}

export default function OfficePolicyDetails() {
  const { t } = useTranslation();
  const history = useHistory();
  const { officePolicyId } = useParams();
  const assignmentId = decodeId(officePolicyId);
  const queryClient = useQueryClient();

  const [commentsList, setCommentsList] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
const {selectedId, getIdToCheckOwnerChange}=useAccountOwnerCommon()
  const {
    data: assignment,
    isLoading: assignmentLoading,
    isFetching: assignmentFetching,
    error: assignmentError,
  } = usePolicyAssignmentById(assignmentId);

  const { data: commentsData } = usePolicyAssignmentComments(
    assignmentId,
    pageNumber,
    COMMENT_PAGE_SIZE,
  );
useEffect(() => {
  if(getIdToCheckOwnerChange()) {
    history.push(constants.routes.officePolicyManagements.officePolicyManagement);
  }
}, [selectedId]);
  useEffect(() => {
    const { rawComments, totalFromApi } = parseCommentsResponse(commentsData);
    if (pageNumber === 1 && rawComments?.length !== undefined) {
      const rows = Array.isArray(rawComments) ? rawComments : [];
      setCommentsList(
        rows.length ? uniqBy(sortCommentsAscending(rows), "id") : [],
      );
      setTotalItems(totalFromApi);
    } else if (pageNumber > 1 && Array.isArray(rawComments)) {
      setCommentsList((prev) =>
        uniqBy(sortCommentsAscending([...prev, ...rawComments]), "id"),
      );
      setTotalItems(totalFromApi);
    }
  }, [pageNumber, commentsData]);

  const goToPreviousScreen = useCallback(() => {
    if (window.history.length > 1) {
      history.goBack();
    } else {
      history.push(
        constants.routes.officePolicyManagements.officePolicyManagement,
      );
    }
  }, [history]);

  const addComments = useCallback(
    async (id, params) => {
      try {
        await addPolicyAssignmentComment(id, params);
        toast.success("Comment added.");
        setPageNumber(1);
        queryClient.invalidateQueries(["policyAssignmentComments", id]);
      } catch (e) {
        toast.error(e?.message ?? "Failed to add comment.");
      }
    },
    [queryClient],
  );

  const anyLoading = assignmentLoading || assignmentFetching;
  const detail = assignment ?? null;

  const assigneeDisplayName = useMemo(() => {
    const u = detail?.user;
    if (!u) return null;
    const name = `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim();
    return name || "—";
  }, [detail?.user]);

  const officesLabel = useMemo(() => {
    const offices = detail?.office ?? [];
    if (!Array.isArray(offices) || offices.length === 0) return null;
    return offices
      .map((o) => o.name ?? o.Name ?? "")
      .filter(Boolean)
      .join(", ");
  }, [detail?.office]);

  if (!assignmentId || assignmentError) {
    return (
      <Page
        onBack={goToPreviousScreen}
        title={t("officePolicyManagement.policyDetails")}
      >
        <Card className={SHELL_CARD_CLASS}>
          <Empty Message={t("officePolicyManagement.policyNotFound")} />
        </Card>
      </Page>
    );
  }

  return (
    <Page
      onBack={goToPreviousScreen}
      title={t("officePolicyManagement.policyDetails")}
    >
      {anyLoading && <Loader />}
      <div className="row no-gutters align-items-center">
        <div className="col-md-7">
          {detail?.user && (
            <Text size="14px" marginBottom="4px" weight="300" color="#000000">
              {assigneeDisplayName}
            </Text>
          )}
          {detail?.office?.name && (
            <Text size="12px" weight="400" color="#6F7788">
              {detail.office.name}
            </Text>
          )}
        </div>
      </div>

      <Card className={POLICY_DETAIL_CARD_CLASS}>
        <PolicyScopeBanner
          appliesToHeading={t("officePolicyManagement.thisPolicyAppliesTo")}
          officesLabel={officesLabel}
        />

        <div
          className={` ${styles["task_detail"]} ${styles["task-policy-details"]} `}
        >
          <LeftSection
            officePolicyDetail={detail}
            assignmentId={assignmentId}
          />
          <RightSection
            assignmentId={assignmentId}
            addComments={addComments}
            setPageNumber={setPageNumber}
            comments={commentsList}
            totalItems={totalItems}
          />
        </div>
      </Card>
    </Page>
  );
}
